// src/views/Publications/PublicationDetail.tsx

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import { useEntity } from '../../context/EntityContext'; // <-- Ajuste conforme onde você obtém a selectedEntity
import { getPublication } from '../../services/publicationsService';
import { actaApi } from '../../services/actaApi';
import { getRelativeFileUrl } from '../../services/relativeFilesService';

/**
 * Formata data/hora em estilo "3 de janeiro de 2025 13:40" (pt-BR).
 * Ajuste conforme seu projeto.
 */
function formatFullDateTime(dateString: string | null) {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(date);
  } catch {
    return dateString;
  }
}

// Cores fixas para as categorias
const catColors = ['primary', 'secondary', 'success', 'error', 'warning', 'info'] as const;

// Cores fixas para "onde" foi publicado
const whereColors = {
  dom: 'info',
  dou: 'warning',
  jgc: 'error',
} as const;

/**
 * Modelo de Publicação (ajuste conforme seu backend).
 */
interface Publication {
  id: string; // Identificador principal no backend
  pdf_path?: string;
  title?: string;
  summary?: string;
  published_at?: string;
  table_of_contents?: { title: string; page: number }[];
  categories?: { id: number | string; category: string }[];
  is_for_dom?: boolean;
  is_for_dou?: boolean;
  is_for_jgc?: boolean;
  uploaded_files_details?: { name: string; url: string; order: number }[];
}

const PublicationDetail: React.FC = () => {
  const { publicationId } = useParams();  // captura /publicacoes/:publicationId
  const { selectedEntity } = useEntity(); // obtém entityId do contexto (ajuste se necessário)
  const navigate = useNavigate();

  // Estados locais
  const [loading, setLoading] = useState(true);
  const [publication, setPublication] = useState<Publication | null>(null);

  // Carregar os dados da publicação ao montar
  useEffect(() => {
    // Se não há publicationId ou selectedEntity, não chamamos a API
    if (!publicationId || !selectedEntity) {
      setLoading(false);
      return;
    }
  
    async function fetchPub() {
      try {
        // Agora o TS sabe que selectedEntity não é null.
        const pubData = await getPublication(selectedEntity!, publicationId!);
        setPublication(pubData);
      } catch (err) {
        console.error('Erro ao buscar publicação:', err);
        swal('Erro', 'Não foi possível carregar os detalhes da publicação.', 'error');
      } finally {
        setLoading(false);
      }
    }
  
    fetchPub();
  }, [publicationId, selectedEntity]);

  if (loading) {
    return (
      <Box sx={{ mt: 5, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Se não encontrou nada, exibe aviso
  if (!publication) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Publicação não encontrada.</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/dashboard')}
          sx={{ mt: 2 }}
        >
          Voltar à Dashboard
        </Button>
      </Box>
    );
  }

  // Montar sumário (caso exista)
  const sumarioString = (publication.table_of_contents || [])
    .map((item) => `• ${item.title} (pág. ${item.page})`)
    .join('\n');

  // Formatar data
  const dateStr = formatFullDateTime(publication.published_at || null);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {publication.title || 'Título não fornecido'}
      </Typography>

      <Typography variant="body1" sx={{ mb: 2 }}>
        Publicado em: {dateStr}
      </Typography>

      <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
        {publication.summary || 'Resumo não fornecido'}
      </Typography>

      {/* Categorias */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {(publication.categories || []).map((cat, idx) => (
          <Chip
            key={cat.id}
            label={cat.category}
            color={catColors[idx % catColors.length]}
            variant="outlined"
          />
        ))}
      </Box>

      {/* Onde foi publicado */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {publication.is_for_dom && (
          <Chip label="Diário Oficial (próprio)" color={whereColors.dom} variant="outlined" />
        )}
        {publication.is_for_dou && (
          <Chip label="Diário Oficial da União" color={whereColors.dou} variant="outlined" />
        )}
        {publication.is_for_jgc && (
          <Chip label="Jornal de Grande Circulação" color={whereColors.jgc} variant="outlined" />
        )}
      </Box>

      {/* Arquivos Originais Carregados */}
      {publication.uploaded_files_details && publication.uploaded_files_details.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Arquivos Originais:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {publication.uploaded_files_details
              // Ordena por 'order' se precisar, caso contrário remove .sort()
              .sort((a, b) => a.order - b.order) 
              .map((file, index) => (
                <li key={index} style={{ marginBottom: '4px' }}>
                  <Typography variant="body2">
                    <a
                      href={getRelativeFileUrl(file.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {file.name}
                    </a>
                  </Typography>
                </li>
              ))}
          </Box>
        </Box>
      )}

      {/* Sumário */}
      {sumarioString && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Sumário:
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {sumarioString}
          </Typography>
        </Box>
      )}

      {/* PDF, ID, Botões */}
      <Box sx={{ mb: 3 }}>
        {/* Botão para acessar PDF, se existir */}
        {publication.pdf_path && (
          <Button
            variant="outlined"
            sx={{ mb: 2 }}
            onClick={() => {
              // Constrói a URL pública usando o ID da publicação e a baseURL do actaApi
              const pdfUrl = `${actaApi.defaults.baseURL}/public/publications/${publication.id}/pdf`;
              window.open(pdfUrl, '_blank');
            }}
          >
            Acessar Publicação (Público)
          </Button>
        )}

        <Typography variant="body2" sx={{ mb: 2 }}>
          Publicação ID: {publication.id}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            Ir para a Dashboard
          </Button>
          <Button variant="outlined" onClick={() => navigate('/publicacoes/nova')}>
            Criar outra Publicação
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PublicationDetail;